import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CCService from "../../services/httpServiceWithoutSuperAdmin";

/**
 * VenueForm — production-ready (OpenStreetMap Nominatim geocoding)
 *
 * - Use for admin to create / edit venues
 * - Nominatim is free but rate-limited; for high-volume apps replace with Google Places or a paid geocoder.
 * - This component presents multiple candidate results and allows the user to choose one.
 *
 * Props:
 * - initialData (optional): object with existing venue fields
 * - onSave (optional): async function(payload) called to persist; should return the saved venue or throw on error
 * - onSuccess (optional): called after successful save (e.g. close modal / refresh)
 */

const schema = yup.object().shape({
  name: yup.string().required("Name required"),
  address_line1: yup.string().required("Address required"),
  city: yup.string().required("City required"),
  country: yup.string().required("Country required"),
  capacity: yup
    .number()
    .typeError("Capacity must be a number")
    .min(0, "Capacity cannot be negative")
    .nullable()
    .transform((v) => (v === "" ? null : v)),
  lat: yup
    .number()
    .transform((value, originalValue) => (originalValue ? Number(value) : null))
    .nullable()
    .typeError("Latitude must be a number")
    .notRequired(),

  lng: yup
    .number()
    .transform((value, originalValue) => (originalValue ? Number(value) : null))
    .nullable()
    .typeError("Longitude must be a number")
    .notRequired(),
  postcode: yup.string().nullable(),
  place_id: yup.string().nullable(),
  external_source: yup.string().nullable(),
});

export default function VenueForm({ initialData = {}, onSuccess }) {
  const services = new CCService();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      place_id: initialData?.place_id ?? "",
      name: initialData?.name ?? "",
      address_line1: initialData?.address_line1 ?? "",
      address_line2: initialData?.address_line2 ?? "",
      city: initialData?.city ?? "",
      state: initialData?.state ?? "",
      country: initialData?.country ?? "IN",
      postcode: initialData?.postcode ?? "",
      lat: initialData?.lat ?? null,
      lng: initialData?.lng ?? null,
      capacity: initialData?.capacity ?? "",
      is_active: initialData?.is_active ?? false,
      external_source: initialData?.external_source ?? "",
    },
    resolver: yupResolver(schema),
  });

  const [lookupStatus, setLookupStatus] = useState(null); // null | loading | ok | error
  const [lookupMessage, setLookupMessage] = useState("");
  const [candidates, setCandidates] = useState([]); // geocode results array
  const [selectedCandidateIdx, setSelectedCandidateIdx] = useState(null);

  console.log("initialData", getValues());
  // Helper: call Nominatim forward geocoding; returns array of candidates or null
  const geocodeNominatim = useCallback(async (query) => {
    if (!query || query.trim().length < 5) {
      setLookupStatus("error");
      setLookupMessage("Please enter a longer address to lookup.");
      return null;
    }
    setLookupStatus("loading");
    setLookupMessage("Looking up coordinates...");
    try {
      // Nominatim requires a polite User-Agent and acceptable use; this is for admin usage.
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(
        query
      )}&addressdetails=1&limit=6`;
      const res = await fetch(url, {
        headers: {
          "User-Agent": "AheadAdmin/1.0 (admin@yourdomain.com)",
          Accept: "application/json",
        },
      });
      if (!res.ok) throw new Error("Geocode request failed");
      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) {
        setLookupStatus("error");
        setLookupMessage("No results found for that address.");
        return null;
      }
      setLookupStatus("ok");
      setLookupMessage(`${data.length} result(s) found — choose one.`);
      return data;
    } catch (err) {
      console.error("geocode error", err);
      setLookupStatus("error");
      setLookupMessage("Geocoding failed. Try again or enter coords manually.");
      return null;
    }
  }, []);

  // Build an address query conveniently from form fields
  const buildAddressQuery = useCallback(() => {
    const vals = getValues();
    return [
      vals.address_line1,
      vals.address_line2,
      vals.city,
      vals.state,
      vals.postcode,
      vals.country,
    ]
      .filter(Boolean)
      .join(", ");
  }, [getValues]);

  // Public: lookup and populate candidate choices
  const handleLookup = async () => {
    const q = buildAddressQuery();
    const results = await geocodeNominatim(q);
    if (!results) {
      setCandidates([]);
      setSelectedCandidateIdx(null);
      return;
    }
    // store candidates and do not auto-pick — let admin pick exact match
    setCandidates(results);
    setSelectedCandidateIdx(null);
  };

  // When admin picks a candidate, populate lat/lng and some fields
  const handlePickCandidate = (idx) => {
    const c = candidates[idx];
    if (!c) return;
    setSelectedCandidateIdx(idx);
    // fields: lat, lng, postcode, address_line1 maybe normalized, city, state, country
    setValue("lat", Number(c.lat));
    setValue("lng", Number(c.lon));
    const addr = c.address || {};
    if (addr.postcode) setValue("postcode", addr.postcode);
    // try to fill common components if empty (do not overwrite if admin already set custom)
    if (!getValues().city && (addr.city || addr.town || addr.village))
      setValue("city", addr.city || addr.town || addr.village);
    if (!getValues().state && addr.state) setValue("state", addr.state);
    if (!getValues().country && addr.country_code)
      setValue("country", (addr.country_code || "").toUpperCase());
    // optionally set normalized display name into address_line1 (commented)
    // setValue("address_line1", c.display_name);
  };

  // Helper to parse a pasted Google Maps URL and store in external_source / place_id (very basic)
  const handleExternalSourceChange = (ev) => {
    const v = ev.target.value || "";
    setValue("external_source", v);
    // Attempt to capture a 'cid' or short place id (best-effort)
    try {
      const u = new URL(v);
      if (u.hostname.includes("google")) {
        // `cid` param is sometimes present; this is naive but useful
        const cid = u.searchParams.get("cid");
        if (cid) setValue("place_id", cid);
      }
    } catch (e) {
      // ignore invalid URL
    }
  };

  // Compose final payload (normalize types)
  const normalizePayload = (data) => {
    return {
      place_id: data.place_id || null,
      name: data.name || null,
      address_line1: data.address_line1 || null,
      address_line2: data.address_line2 || null,
      city: data.city || null,
      state: data.state || null,
      country: data.country || null,
      postcode: data.postcode || null,
      lat: data.lat === "" || data.lat === null ? null : Number(data.lat),
      lng: data.lng === "" || data.lng === null ? null : Number(data.lng),
      capacity:
        data.capacity === "" || data.capacity === null
          ? null
          : Number(data.capacity),
      is_active: !!data.is_active,
      external_source: data.external_source || null,
    };
  };

  // Submit handler: uses onSave prop if provided; otherwise logs payload
  const onSubmit = async (raw) => {
    const payload = normalizePayload(raw);
    if (initialData?.id) {
      const res = await services.put(
        `/ahead/event-venues/${initialData.id}`,
        payload
      );

      console.log("res", res);

      if (onSuccess) {
        onSuccess(res.data.venue, "update");
      }
    } else {
      const response = await services.post(`/ahead/event-venues`, payload);

      if (onSuccess) {
        onSuccess(response.data.venue, "post");
      }
    }
  };

  // Render a small friendly candidate list when candidates exist
  const candidateList = useMemo(() => {
    if (!Array.isArray(candidates) || candidates.length === 0) return null;
    return (
      <div
        style={{
          marginTop: 8,
          border: "1px solid #e6edf3",
          borderRadius: 8,
          padding: 8,
          background: "#fff",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
          Address matches
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {candidates.map((c, i) => {
            const address = c.display_name || `${c.lat},${c.lon}`;
            const isSelected = selectedCandidateIdx === i;
            return (
              <button
                key={c.place_id || i}
                type="button"
                onClick={() => handlePickCandidate(i)}
                style={{
                  textAlign: "left",
                  padding: 10,
                  borderRadius: 8,
                  border: isSelected
                    ? "1px solid #374151"
                    : "1px solid #eef2f6",
                  background: isSelected ? "#f8fafc" : "white",
                  cursor: "pointer",
                }}
                aria-pressed={isSelected}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{address}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Lat: {Number(c.lat).toFixed(6)} · Lng:{" "}
                  {Number(c.lon).toFixed(6)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [candidates, selectedCandidateIdx]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ maxWidth: 840, padding: 12 }}
    >
      <div
        style={{
          background: "#fff",
          padding: 16,
          borderRadius: 10,
          boxShadow: "0 6px 24px rgba(15,23,42,0.04)",
        }}
      >
        <h3 style={{ margin: 0, marginBottom: 12 }}>Venue details</h3>

        <div style={{ display: "grid", gap: 10 }}>
          <input
            {...register("name")}
            placeholder="Venue name"
            style={inputStyle}
            aria-label="Venue name"
          />
          {errors.name && <div style={errorStyle}>{errors.name.message}</div>}

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
          >
            <input
              {...register("city")}
              placeholder="City"
              style={inputStyle}
              aria-label="City"
            />
            <input
              {...register("state")}
              placeholder="State"
              style={inputStyle}
              aria-label="State"
            />
          </div>
          {errors.city && <div style={errorStyle}>{errors.city.message}</div>}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 220px",
              gap: 10,
            }}
          >
            <input
              {...register("country")}
              placeholder="Country (ISO code, e.g., IN)"
              style={inputStyle}
              aria-label="Country"
            />
            <input
              {...register("postcode")}
              placeholder="Postcode"
              style={inputStyle}
              aria-label="Postcode"
            />
          </div>

          <input
            {...register("address_line1")}
            placeholder="Address line 1"
            style={inputStyle}
            aria-label="Address line 1"
          />
          {errors.address_line1 && (
            <div style={errorStyle}>{errors.address_line1.message}</div>
          )}

          <input
            {...register("address_line2")}
            placeholder="Address line 2"
            style={inputStyle}
            aria-label="Address line 2"
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10,
            }}
          >
            <input
              {...register("lat")}
              placeholder="Latitude"
              style={inputStyle}
            />
            <input
              {...register("lng")}
              placeholder="Longitude"
              style={inputStyle}
            />
            <input
              {...register("capacity")}
              placeholder="Capacity"
              type="number"
              style={inputStyle}
            />
          </div>
          {errors.lat && <div style={errorStyle}>{errors.lat.message}</div>}
          {errors.lng && <div style={errorStyle}>{errors.lng.message}</div>}
          {errors.capacity && (
            <div style={errorStyle}>{errors.capacity.message}</div>
          )}

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={handleLookup}
              style={{
                padding: "8px 12px",
                background: "#374151",
                color: "white",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
              }}
            >
              Lookup coordinates
            </button>

            <div
              style={{
                color: lookupStatus === "error" ? "#dc2626" : "#6b7280",
              }}
            >
              {lookupMessage}
            </div>

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="checkbox"
                  {...register("is_active")}
                  defaultChecked={initialData?.is_active ?? true}
                />
                <span style={{ color: "#374151" }}>Active</span>
              </label>
            </div>
          </div>

          {/* Candidates list (if any) */}
          {candidateList}

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}
          >
            <input
              {...register("external_source")}
              onChange={handleExternalSourceChange}
              placeholder="External source URL (optional)"
              style={inputStyle}
            />
            <input
              {...register("place_id")}
              placeholder="place_id (optional)"
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: "10px 12px",
                background: "#0f1724",
                color: "#fff",
                borderRadius: 8,
                border: "none",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                fontWeight: 700,
              }}
            >
              {isSubmitting ? "Saving..." : "Save venue"}
            </button>

            <button
              type="button"
              onClick={() => {
                reset(); // clear to defaults
                setCandidates([]);
                setSelectedCandidateIdx(null);
                setLookupMessage("");
                setLookupStatus(null);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #eef2f6",
                background: "#fff",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

/* ---- small inline styles used above ---- */
const inputStyle = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #E5E7EB",
  boxSizing: "border-box",
  outline: "none",
  width: "100%",
};

const errorStyle = {
  color: "#dc2626",
  fontSize: 13,
  marginTop: -6,
  marginBottom: 6,
};
